import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { positionMenuAtPoint } from '../utils/menuPosition';
import { ReactComponent as PlayIcon } from '../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../assets/icons/pause.svg';
import { ReactComponent as LoopIcon } from '../assets/icons/loop.svg';

// Cache decoded + band-analyzed waveform data per source URL.
const waveCache = new Map(); // src -> { cols:{low,mid,high,amp}, duration }

// Whole-file fetch for decoding. `no-store` is REQUIRED: the bottom <audio>
// player streams the same URL with Range requests, which leaves a sparse entry
// in Chromium's HTTP cache — a plain full-body fetch of that URL then fails with
// net::ERR_CACHE_OPERATION_NOT_SUPPORTED ("Could not load audio", intermittent,
// works on retry). Bypassing the cache sidesteps the sparse entry; the file is
// local and the decoded result is memoized below anyway.
async function fetchWholeFile(src) {
    const resp = await fetch(src, { cache: 'no-store' });
    if (!resp.ok) throw new Error(`audio fetch failed: ${resp.status}`);
    return resp.arrayBuffer();
}

// Decoded AudioBuffer for the CURRENT track only (used for gapless loop preview
// via the Web Audio API). Kept to one entry to bound memory.
let _bufCache = { src: null, buffer: null };
async function getAudioBuffer(src) {
    if (_bufCache.src === src && _bufCache.buffer) return _bufCache.buffer;
    const arr = await fetchWholeFile(src);
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const buffer = await ctx.decodeAudioData(arr);
    try { ctx.close(); } catch (e) { /* noop */ }
    _bufCache = { src, buffer };
    return buffer;
}
const ANALYZE_COLUMNS = 2400; // fixed high-res analysis; canvas scales with zoom
const MIN_ZOOM = 1;
const MAX_ZOOM = 16;
const EDGE_FRAC = 0.015; // inset the timeline a touch on both ends so a 0:00 cue is visible

const LOOP_KIND_LABEL = {
    intro: 'Intro', outro: 'Outro', drop: 'Drop', break: 'Break',
    beat: 'Groove', section: 'Section', cue: 'Loop',
};
function loopLabel(loop) {
    const kind = LOOP_KIND_LABEL[loop.kind] || (loop.name ? loop.name.split(' · ')[0] : 'Loop');
    return loop.bars ? `${kind} · ${loop.bars} bars` : kind;
}

function formatTime(sec) {
    if (!sec || isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

async function analyzeAudio(src) {
    if (waveCache.has(src)) return waveCache.get(src);
    const arrayBuf = await fetchWholeFile(src);
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const audioBuf = await ctx.decodeAudioData(arrayBuf);
    const duration = audioBuf.duration;

    const renderBand = async (type, freq, q) => {
        const off = new OfflineAudioContext(1, audioBuf.length, audioBuf.sampleRate);
        const node = off.createBufferSource();
        node.buffer = audioBuf;
        const filter = off.createBiquadFilter();
        filter.type = type;
        filter.frequency.value = freq;
        if (q != null) filter.Q.value = q;
        node.connect(filter); filter.connect(off.destination); node.start(0);
        return (await off.startRendering()).getChannelData(0);
    };

    const [low, mid, high] = await Promise.all([
        renderBand('lowpass', 200), renderBand('bandpass', 1200, 0.8), renderBand('highpass', 4000),
    ]);
    const full = audioBuf.getChannelData(0);
    ctx.close();

    const columns = ANALYZE_COLUMNS;
    const total = full.length;
    const per = Math.max(1, Math.floor(total / columns));
    const cols = { low: new Float32Array(columns), mid: new Float32Array(columns), high: new Float32Array(columns), amp: new Float32Array(columns) };
    const rms = (arr, s, e) => { let x = 0; for (let i = s; i < e; i++) x += arr[i] * arr[i]; return Math.sqrt(x / Math.max(1, e - s)); };
    for (let c = 0; c < columns; c++) {
        const s = c * per, e = Math.min(total, s + per);
        cols.low[c] = rms(low, s, e); cols.mid[c] = rms(mid, s, e); cols.high[c] = rms(high, s, e); cols.amp[c] = rms(full, s, e);
    }
    const normMax = (a) => { let m = 0; for (let i = 0; i < a.length; i++) if (a[i] > m) m = a[i]; if (m > 0) for (let i = 0; i < a.length; i++) a[i] /= m; };
    normMax(cols.low); normMax(cols.mid); normMax(cols.high);
    const sortedAmp = Float32Array.from(cols.amp).sort();
    const ref = sortedAmp[Math.floor(sortedAmp.length * 0.98)] || sortedAmp[sortedAmp.length - 1] || 1;
    for (let i = 0; i < cols.amp.length; i++) cols.amp[i] = Math.min(1, cols.amp[i] / ref);

    const result = { cols, duration };
    waveCache.set(src, result);
    return result;
}

// DESIGN V2 palette: two tones read from the theme (lows in the tertiary
// text colour, highs in the accent light at 60%) — only the cue flags stay
// coloured. Resolved per draw so a theme switch repaints correctly.
function twoTonePalette() {
    const css = getComputedStyle(document.documentElement);
    const low = (css.getPropertyValue('--color-text-tertiary') || '').trim() || '#7a736a';
    const high = (css.getPropertyValue('--color-accent-light') || '').trim() || '#ffd08a';
    return { low, high };
}

function drawWaveform(canvas, cols, twoTone = false) {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    canvas.width = w * dpr; canvas.height = h * dpr;
    const c2 = canvas.getContext('2d');
    c2.setTransform(dpr, 0, 0, dpr, 0, 0);
    c2.clearRect(0, 0, w, h);
    const n = cols.amp.length;
    const mid = h / 2;
    const x0 = w * EDGE_FRAC;
    const innerW = w * (1 - 2 * EDGE_FRAC);
    const colW = innerW / n;
    if (twoTone) {
        const pal = twoTonePalette();
        for (let c = 0; c < n; c++) {
            const amp = Math.pow(cols.amp[c], 1.4);
            const barH = Math.max(1, amp * (h * 0.92));
            const x = x0 + c * colW;
            const bw = Math.max(0.6, colW * 0.9);
            // body: the lows/mids in the quiet tone
            c2.globalAlpha = 1;
            c2.fillStyle = pal.low;
            c2.fillRect(x, mid - barH / 2, bw, barH);
            // highs: a shorter bar in the accent, 60% — the air of the track
            const hi = Math.max(0, barH * Math.min(1, cols.high[c]));
            if (hi > 0.5) {
                c2.globalAlpha = 0.6;
                c2.fillStyle = pal.high;
                c2.fillRect(x, mid - hi / 2, bw, hi);
            }
        }
        c2.globalAlpha = 1;
        return;
    }
    for (let c = 0; c < n; c++) {
        const r = Math.min(255, Math.round(40 + cols.high[c] * 235));
        const g = Math.min(255, Math.round(30 + cols.mid[c] * 215));
        const b = Math.min(255, Math.round(70 + cols.low[c] * 205));
        c2.fillStyle = `rgb(${r},${g},${b})`;
        const amp = Math.pow(cols.amp[c], 1.4);
        const barH = Math.max(1, amp * (h * 0.92));
        c2.fillRect(x0 + c * colW, mid - barH / 2, Math.max(0.6, colW * 0.9), barH);
    }
}

/**
 * Frequency-colored waveform with zoom, draggable cue markers, shaded loop
 * regions, and a transport strip (play / add / cue pads / time). Playback uses
 * the app's global player.
 *
 * Props: src, cues, loops, onAddCue(ms), onMoveCue(id,ms),
 *        onAddLoop(startMs,endMs?), onMoveLoop(id,startMs), onResizeLoop(id,edge,ms),
 *        isActive, isPlaying, onTogglePlay, onSeek(ms),
 *        timelineOffsetMs (see below), children (cue/loop pads rendered in the strip)
 *
 * Timelines: every ms in and out of this component is on the DJ apps' decoder
 * timeline (what Rekordbox/Serato store and play). The decoded waveform and
 * the Web Audio loop preview run on the gapless timeline, which is
 * timelineOffsetMs EARLIER (up to 51 ms on LAME MP3s — scripts/mp3_timeline.py).
 * The conversion happens once, right here: draw/play at (ms - offset), hand
 * every edit back at (ms + offset).
 */
const ColoredWaveform = ({
    src, cues: cuesApp = [], loops: loopsApp = [], activeLoopId = null, loopPosRef, onAddCue, onMoveCue,
    onAddLoop, onMoveLoop, onResizeLoop, hoveredId, onHoverItem, audioLatencyRef, exitIntentRef, onLoopExit,
    isActive, isPlaying, onTogglePlay, onSeek, timelineOffsetMs = 0, children,
    // DESIGN V2: two-tone bars, and the zoom control rendered into a toolbar
    // slot (a ref to an element) instead of floating over the waveform.
    twoTone = false, zoomSlotRef = null,
}) => {
    const tlOff = Number(timelineOffsetMs) || 0;
    const shiftItems = (items) => (items || []).map((it) => ({
        ...it,
        positionMs: it.positionMs - tlOff,
        endPositionMs: it.endPositionMs != null ? it.endPositionMs - tlOff : it.endPositionMs,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const cues = useMemo(() => (tlOff ? shiftItems(cuesApp) : cuesApp), [cuesApp, tlOff]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const loops = useMemo(() => (tlOff ? shiftItems(loopsApp) : loopsApp), [loopsApp, tlOff]);
    const wrapRef = useRef(null);    // scroll viewport
    const contentRef = useRef(null); // zoomed content (width = zoom * viewport)
    const canvasRef = useRef(null);
    const playheadRef = useRef(null);
    const dataRef = useRef(null);
    const dragRef = useRef(null);
    const refs = useRef({});
    const toApp = (ms) => Math.round(ms + tlOff);
    refs.current = {
        onAddCue: onAddCue ? (ms, ...rest) => onAddCue(toApp(ms), ...rest) : onAddCue,
        onMoveCue: onMoveCue ? (id, ms) => onMoveCue(id, toApp(ms)) : onMoveCue,
        onSeek: onSeek ? (ms) => onSeek(toApp(ms)) : onSeek,
        onAddLoop: onAddLoop ? (s, e) => onAddLoop(toApp(s), e != null ? toApp(e) : e) : onAddLoop,
        onMoveLoop: onMoveLoop ? (id, ms) => onMoveLoop(id, toApp(ms)) : onMoveLoop,
        onResizeLoop: onResizeLoop ? (id, edge, ms) => onResizeLoop(id, edge, toApp(ms)) : onResizeLoop,
    };

    const [status, setStatus] = useState('loading');
    // DESIGN V2: the zoom chip is portalled into the editor toolbar. The slot
    // mounts in the same commit as this component, so read the ref after layout.
    const [zoomSlotEl, setZoomSlotEl] = useState(null);
    useLayoutEffect(() => { setZoomSlotEl(zoomSlotRef?.current || null); }, [zoomSlotRef]);
    const [duration, setDuration] = useState(0);
    const [curTime, setCurTime] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [drawLoop, setDrawLoop] = useState(null); // {startMs, endMs} live shift-drag preview
    const [ctxMenu, setCtxMenu] = useState(null);   // right-click add menu {x, y, ms}
    const ctxMenuRef = useRef(null);
    const [ctxMenuPos, setCtxMenuPos] = useState(null); // clamped {left, top}; null until measured

    // Position helpers shared by markers, regions and the playhead.
    const msToPct = useCallback((ms) => {
        const dur = dataRef.current?.duration || duration || 0;
        const frac = dur ? Math.min(1, Math.max(0, ms / 1000 / dur)) : 0;
        return (EDGE_FRAC + frac * (1 - 2 * EDGE_FRAC)) * 100;
    }, [duration]);

    // ---- gapless loop preview (Web Audio); normal playback uses the bottom player ----
    const playCtxRef = useRef(null);
    const loopNodeRef = useRef(null);
    const loopGainRef = useRef(null);
    const volListenerRef = useRef(null);
    const loopRef = useRef(null); // { startSec, endSec, startedAt, linear? }
    const handleTransportRef = useRef(() => {});
    const [loopPlaying, setLoopPlaying] = useState(false);
    // --- Web Audio source lifecycle (Option C: stay in Web Audio through the loop
    // AND after deactivate, until a deliberate pause/seek/track-change) ---
    const waModeRef = useRef(null);        // 'loop' | 'linear' | null
    const handingBackRef = useRef(false);
    const mirrorSeekTsRef = useRef(0);     // when WE last mirror-seeked the player
    const seekListenerRef = useRef(null);

    // Fully hand control back to the bottom player: fade+stop the Web Audio node and
    // unmute the (mirror-synced) player. reason: 'pause' | 'seek' | 'track'.
    const handBackFromWA = useCallback((reason) => {
        if (handingBackRef.current || !waModeRef.current) return;
        handingBackRef.current = true;
        const gp = window.musicPlayerInstance;
        const node = loopNodeRef.current;
        const gain = loopGainRef.current;
        const ctx = playCtxRef.current;
        waModeRef.current = null;
        loopNodeRef.current = null; loopGainRef.current = null;
        loopRef.current = null;
        setLoopPlaying(false);
        if (gp && seekListenerRef.current) { try { gp.removeEventListener('seeking', seekListenerRef.current); } catch (e) { /* noop */ } seekListenerRef.current = null; }
        if (gp && volListenerRef.current) { try { gp.removeEventListener('volumechange', volListenerRef.current); } catch (e) { /* noop */ } volListenerRef.current = null; }
        if (node && ctx) {
            try {
                const t = ctx.currentTime;
                if (gain) { gain.gain.cancelScheduledValues(t); gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), t); gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05); }
                node.stop(t + 0.06);
                node.onended = () => { try { node.disconnect(); if (gain) gain.disconnect(); } catch (e) { /* noop */ } };
            } catch (e) { /* noop */ }
        }
        if (gp) {
            try {
                gp.muted = false;                 // restore audibility
                if (reason === 'track') gp.pause(); // navigated away -> don't blare
                // NOTE: never force gp.play() here — preserve the current play/pause
                // state (during a loop it's already playing; if the user paused, stay
                // paused; leaving the screen must NOT start a paused track).
            } catch (e) { /* noop */ }
        }
        handingBackRef.current = false;
        if (onLoopExit) onLoopExit();
    }, [onLoopExit]);

    // Stop with a 15ms fade-out so the node doesn't end on a non-zero sample (= click).
    const stopLoopNode = useCallback(() => {
        const node = loopNodeRef.current;
        const gain = loopGainRef.current;
        const ctx = playCtxRef.current;
        loopNodeRef.current = null;
        loopGainRef.current = null;
        if (!node) return;
        try {
            if (gain && ctx) {
                const t = ctx.currentTime;
                gain.gain.cancelScheduledValues(t);
                gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), t);
                gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.015);
                node.stop(t + 0.02);
                node.onended = () => { try { node.disconnect(); gain.disconnect(); } catch (e) { /* noop */ } };
            } else {
                node.stop(); node.disconnect();
            }
        } catch (e) { /* noop */ }
    }, []);

    // Clicking INSIDE the active loop: move playback to that point but KEEP looping
    // (restart the loop node at the clicked offset).
    const repositionLoop = useCallback((ms) => {
        const ctx = playCtxRef.current;
        const li = loopRef.current;
        if (!ctx || !li || li.linear || waModeRef.current !== 'loop' || !src) return;
        const offset = Math.max(li.startSec, Math.min(li.endSec - 0.02, ms / 1000));
        getAudioBuffer(src).then((buffer) => {
            if (loopRef.current !== li || waModeRef.current !== 'loop') return;
            const gp = window.musicPlayerInstance;
            const vol = (gp && typeof gp.volume === 'number' && gp.volume >= 0) ? gp.volume : 1;
            stopLoopNode();
            const gain = ctx.createGain();
            gain.gain.setValueAtTime(0.0001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, vol), ctx.currentTime + 0.012);
            const node = ctx.createBufferSource();
            node.buffer = buffer;
            node.loop = true; node.loopStart = li.startSec; node.loopEnd = li.endSec;
            node.connect(gain); gain.connect(ctx.destination);
            node.start(0, offset);
            loopNodeRef.current = node; loopGainRef.current = gain;
            loopRef.current = { ...li, startedAt: ctx.currentTime - (offset - li.startSec) };
        }).catch(() => { /* noop */ });
    }, [src, stopLoopNode]);

    const activeLoop = (loops || []).find((l) => l.id === activeLoopId) || null;
    const aStart = activeLoop ? activeLoop.positionMs : null;
    const aEnd = activeLoop ? activeLoop.endPositionMs : null;
    const aEndRef = useRef(aEnd);
    aEndRef.current = aEnd;
    const srcRef = useRef(src);
    srcRef.current = src;
    const activeLoopIdRef = useRef(activeLoopId);
    activeLoopIdRef.current = activeLoopId;

    // Warm the decoded-buffer cache when a track loads, so the FIRST loop activation
    // doesn't stall on a multi-hundred-ms decode (that was the audible "jump").
    useEffect(() => {
        if (src) getAudioBuffer(src).catch(() => {});
    }, [src]);
    // Create the AudioContext eagerly so activation doesn't pay its setup cost.
    useEffect(() => {
        if (!playCtxRef.current) {
            try {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                playCtxRef.current = new Ctx();
            } catch (e) { /* noop */ }
        }
    }, []);

    // Start gapless Web Audio looping (deps: activeLoopId + START + src only — NOT
    // the end, so resizing/×2/÷2 doesn't restart the loop). Pauses the bottom
    // player. The cleanup does NOT auto-resume the bottom player (that caused stray
    // playback when leaving a track); exit/resume is handled explicitly.
    useEffect(() => {
        if (activeLoopId == null || aStart == null || !src) return undefined;
        let cancelled = false;
        (async () => {
            // Prepare everything that can block (decode + context resume) FIRST,
            // while the bottom player keeps playing — so the actual handover is one
            // synchronous block with no silent gap (that gap was the "flicker").
            let buffer;
            try { buffer = await getAudioBuffer(src); } catch (e) { return; }
            if (cancelled) return;
            if (!playCtxRef.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                playCtxRef.current = new Ctx();
            }
            const ctx = playCtxRef.current;
            if (ctx.state === 'suspended') { try { await ctx.resume(); } catch (e) { /* noop */ } }
            if (cancelled) return;
            // --- synchronous handover (no awaits past this point) ---
            const gp = window.musicPlayerInstance;
            const s = aStart / 1000;
            const e = (aEndRef.current || aStart) / 1000;
            // A loop needs a length. With e <= s the node would loop the whole
            // buffer while the playhead mirror seeks the muted bottom player back
            // to `s` every frame — a seek storm that ends in a media error and an
            // ejected track. Refuse to enter Web Audio mode instead.
            if (!(e - s > 0.01)) { if (onLoopExit) onLoopExit(); return; }
            // Start from the current playhead if it's already inside the loop (so
            // activating doesn't yank the cursor to the loop start); else from start.
            // Add the output latency: gp.currentTime is what you HEAR now, but the
            // node's audio isn't audible until `latency` later — without this the
            // loop replays the last 100-500ms (a backward "record skip").
            const latency = (typeof ctx.outputLatency === 'number' && ctx.outputLatency > 0)
                ? ctx.outputLatency
                : (ctx.baseLatency || 0);
            if (audioLatencyRef) audioLatencyRef.current = latency; // share for the exit handoff
            const cur = (gp && gp.currentTime) || 0;
            const base = (cur > s && cur < e) ? cur : s;
            const startAt = Math.min(e - 0.02, base + latency);
            stopLoopNode();
            const node = ctx.createBufferSource();
            node.buffer = buffer;
            node.loop = true; node.loopStart = s; node.loopEnd = e;
            // Fade in over 15ms through a gain node so the start doesn't pop. The
            // loop must play at the PLAYER'S volume, not full (else it jumps loud).
            const vol = (gp && typeof gp.volume === 'number' && gp.volume >= 0) ? gp.volume : 1;
            const gain = ctx.createGain();
            gain.gain.setValueAtTime(0.0001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, vol), ctx.currentTime + 0.01);
            node.connect(gain); gain.connect(ctx.destination);
            node.start(0, startAt);
            loopGainRef.current = gain;
            // MUTE the bottom player rather than pausing it — muting is instant, so
            // there's no rebuffer gap on the way in or out (pausing/resuming an
            // <audio> element stalls for ~0.5s). Keep it warm by letting it play.
            if (gp) {
                try {
                    gp.muted = true; if (gp.paused) gp.play().catch(() => {});
                    // track the volume slider live so the loop follows it
                    if (!volListenerRef.current) {
                        const onVol = () => {
                            const g = loopGainRef.current;
                            if (g && playCtxRef.current) {
                                try { g.gain.setValueAtTime(Math.max(0.0001, gp.volume), playCtxRef.current.currentTime); } catch (e) { /* noop */ }
                            }
                        };
                        volListenerRef.current = onVol;
                        gp.addEventListener('volumechange', onVol);
                    }
                    // watch for the user SEEKING the player (editor or global bar) so we
                    // hand back; our own mirror-seeks are ignored via mirrorSeekTsRef.
                    if (!seekListenerRef.current) {
                        const onSeeking = () => {
                            if (waModeRef.current && !handingBackRef.current
                                && (performance.now() - mirrorSeekTsRef.current) > 160) {
                                handBackFromWA('seek');
                            }
                        };
                        seekListenerRef.current = onSeeking;
                        gp.addEventListener('seeking', onSeeking);
                    }
                } catch (e) { /* noop */ }
            }
            waModeRef.current = 'loop';
            loopNodeRef.current = node;
            loopRef.current = { startSec: s, endSec: e, startedAt: ctx.currentTime - (startAt - s) };
            setLoopPlaying(true);
        })();
        const setupSrc = src;
        return () => {
            cancelled = true;
            const node = loopNodeRef.current;
            const ctx = playCtxRef.current;
            const li = loopRef.current;
            const goingToNull = activeLoopIdRef.current == null;      // exit vs re-activate
            const intent = exitIntentRef ? exitIntentRef.current : 'track';
            const trackChanged = srcRef.current !== setupSrc;

            if (goingToNull && intent === 'resume' && !trackChanged && node && ctx && li && waModeRef.current === 'loop') {
                // DEACTIVATE -> go LINEAR: keep the SAME node playing, just stop
                // looping. Zero switch, zero jump. The bottom player keeps mirroring;
                // a later pause/seek/track-change triggers the reactive handback.
                try {
                    node.loop = false;
                    const len = li.endSec - li.startSec;
                    const curPos = len > 0 ? li.startSec + ((ctx.currentTime - li.startedAt) % len) : li.startSec;
                    loopRef.current = { startSec: curPos, endSec: null, startedAt: ctx.currentTime, linear: true };
                    waModeRef.current = 'linear';
                } catch (e) { handBackFromWA('track'); }
            } else if (goingToNull && (trackChanged || intent === 'seek')) {
                // deliberate exit / track change -> full handback (stop the WA node)
                handBackFromWA(trackChanged ? 'track' : 'seek');
            }
            // else (re-activation to another loop): the setup already stopped the old
            // node and started the new one; nothing to tear down here.
        };
    }, [activeLoopId, aStart, src, stopLoopNode, handBackFromWA]);

    // Resize the active loop live (×2 / ÷2 / drag the end) WITHOUT restarting — just
    // update loopEnd on the playing node, so playback never jumps to the start. Keep
    // the playhead continuous across the length change (no visual desync).
    useEffect(() => {
        const node = loopNodeRef.current; const li = loopRef.current; const ctx = playCtxRef.current;
        if (node && li && ctx && aEnd != null) {
            const newE = aEnd / 1000;
            const oldLen = li.endSec - li.startSec;
            const curPos = oldLen > 0 ? li.startSec + ((ctx.currentTime - li.startedAt) % oldLen) : li.startSec;
            node.loopEnd = newE;
            const newLen = Math.max(newE - li.startSec, 0.01);
            const posInLoop = Math.min(Math.max(curPos - li.startSec, 0), newLen - 0.001);
            loopRef.current = { ...li, endSec: newE, startedAt: ctx.currentTime - posInLoop };
        }
    }, [aEnd]);

    useEffect(() => () => {
        stopLoopNode();
        const gp = window.musicPlayerInstance;
        if (gp) {
            try {
                if (seekListenerRef.current) gp.removeEventListener('seeking', seekListenerRef.current);
                if (volListenerRef.current) gp.removeEventListener('volumechange', volListenerRef.current);
                // Leaving the screen: just unmute — the player then FOLLOWS ITS STATE
                // (Web Audio kept it playing while looping -> keeps playing; if you'd
                // paused, it's already paused -> stays paused). Don't force pause/play.
                if (waModeRef.current) gp.muted = false;
            } catch (e) { /* noop */ }
        }
        seekListenerRef.current = null; volListenerRef.current = null; waModeRef.current = null;
        if (playCtxRef.current) { try { playCtxRef.current.close(); } catch (e) { /* noop */ } playCtxRef.current = null; }
    }, [stopLoopNode]);

    // Transport: while a loop is active, suspend/resume the loop; otherwise the
    // bottom player (via the parent).
    const handleTransport = useCallback(() => {
        if (activeLoopId != null && playCtxRef.current) {
            const ctx = playCtxRef.current;
            if (ctx.state === 'running') { ctx.suspend(); setLoopPlaying(false); }
            else { ctx.resume(); setLoopPlaying(true); }
            return;
        }
        if (onTogglePlay) onTogglePlay();
    }, [activeLoopId, onTogglePlay]);
    handleTransportRef.current = handleTransport;

    // Keyboard: while a loop is active, space/enter toggles the loop (the global
    // handler covers normal bottom-player playback).
    useEffect(() => {
        const onKey = (e) => {
            const tag = (e.target && e.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;
            if (e.code === 'Space' || e.key === ' ' || e.code === 'Enter' || e.key === 'Enter') {
                if (activeLoopId != null) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    handleTransportRef.current();
                }
            }
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [activeLoopId]);

    // Decode + analyze.
    useEffect(() => {
        let cancelled = false;
        setStatus('loading'); setZoom(1); dataRef.current = null;
        if (!src) { setStatus('error'); return undefined; }
        (async () => {
            try {
                const data = await analyzeAudio(src);
                if (cancelled) return;
                dataRef.current = data;
                setDuration(data.duration);
                setStatus('ready');
                requestAnimationFrame(() => { if (!cancelled && canvasRef.current) drawWaveform(canvasRef.current, data.cols, twoTone); });
            } catch (e) { if (!cancelled) { console.error('[ColoredWaveform]', e); setStatus('error'); } }
        })();
        return () => { cancelled = true; };
    // twoTone is handled by the redraw effects below — a palette switch must not re-decode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    // Redraw whenever the content width changes (zoom or container resize).
    useEffect(() => {
        const redraw = () => { if (dataRef.current && canvasRef.current) drawWaveform(canvasRef.current, dataRef.current.cols, twoTone); };
        const ro = new ResizeObserver(redraw);
        if (contentRef.current) ro.observe(contentRef.current);
        return () => ro.disconnect();
    }, [twoTone]);
    useEffect(() => {
        if (dataRef.current && canvasRef.current) requestAnimationFrame(() => drawWaveform(canvasRef.current, dataRef.current.cols, twoTone));
    }, [zoom, twoTone]);

    // Trackpad pinch (ctrl+wheel) → zoom; non-passive so we can preventDefault.
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const onWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * (1 - e.deltaY * 0.01))));
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    // Playhead: the active loop position (Web Audio), else the bottom player.
    useEffect(() => {
        let raf;
        const tick = () => {
            const dur = dataRef.current?.duration || 0;
            let t = 0;
            const li = loopRef.current;
            const ctx = playCtxRef.current;
            if (li && ctx) {
                if (li.linear) {
                    t = li.startSec + (ctx.currentTime - li.startedAt);   // continue linearly
                    if (dur && t >= dur - 0.02) { handBackFromWA('track'); } // buffer ended
                } else {
                    const len = li.endSec - li.startSec;
                    const elapsed = ctx.currentTime - li.startedAt;
                    t = len > 0 ? li.startSec + (elapsed % len) : li.startSec;
                }
                if (loopPosRef) loopPosRef.current = t;
                const gp = window.musicPlayerInstance;
                if (gp && waModeRef.current) {
                    // Reactive: the user paused the player (editor OR global bar) ->
                    // hand back (the mirror never pauses it, so paused == user action).
                    if (gp.paused && !handingBackRef.current) {
                        handBackFromWA('pause');
                    } else if (gp.muted) {
                        // Mirror the player to the loop so a later handback is a no-seek
                        // unmute. Mirror to (t - outputLatency): `t` is the node's OUTPUT
                        // position (heard L_wa later) but the element's currentTime IS
                        // its heard position. Tag the seek so our own 'seeking' events
                        // aren't mistaken for a user seek.
                        const lat = (typeof ctx.outputLatency === 'number' && ctx.outputLatency > 0)
                            ? ctx.outputLatency : (ctx.baseLatency || 0);
                        const target = t - lat;
                        const gpc = gp.currentTime || 0;
                        if (target >= 0 && Math.abs(gpc - target) > 0.05) {
                            mirrorSeekTsRef.current = performance.now();
                            try { gp.currentTime = target; } catch (e) { /* noop */ }
                        }
                    }
                }
            } else if (isActive && window.musicPlayerInstance) {
                t = window.musicPlayerInstance.currentTime || 0;
            }
            if (playheadRef.current && dur) {
                const frac = Math.min(1, Math.max(0, t / dur));
                playheadRef.current.style.left = `${(EDGE_FRAC + frac * (1 - 2 * EDGE_FRAC)) * 100}%`;
            }
            setCurTime((li || isActive) ? t : 0);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [isActive, handBackFromWA]);

    const xToMs = useCallback((clientX) => {
        const rect = contentRef.current.getBoundingClientRect();
        const frac = (clientX - rect.left) / rect.width;
        const pos = Math.min(1, Math.max(0, (frac - EDGE_FRAC) / (1 - 2 * EDGE_FRAC)));
        return Math.round(pos * (dataRef.current?.duration || 0) * 1000);
    }, []);

    // Drag the waveform body to pan (scroll horizontally); a plain click seeks;
    // a double-click only adds a cue (no seek / no playback).
    const clickTimer = useRef(null);
    const panRef = useRef(null);
    const didPan = useRef(false);

    const onContentPointerDown = useCallback((e) => {
        if (e.button !== 0 || !dataRef.current || !wrapRef.current) return;

        // Shift-drag draws a new loop region (instead of panning).
        if (e.shiftKey && refs.current.onAddLoop) {
            e.preventDefault();
            const startMs = xToMs(e.clientX);
            setDrawLoop({ startMs, endMs: startMs });
            const move = (ev) => setDrawLoop({ startMs, endMs: xToMs(ev.clientX) });
            const up = (ev) => {
                const endMs = xToMs(ev.clientX);
                const lo = Math.min(startMs, endMs);
                const hi = Math.max(startMs, endMs);
                setDrawLoop(null);
                didPan.current = true; // suppress the trailing seek-click
                if (hi - lo >= 200) refs.current.onAddLoop(lo, hi);
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
            };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
            return;
        }

        panRef.current = { startX: e.clientX, startScroll: wrapRef.current.scrollLeft, moved: false };
        if (wrapRef.current) wrapRef.current.classList.add('panning');
        const move = (ev) => {
            if (!panRef.current) return;
            const dx = ev.clientX - panRef.current.startX;
            if (Math.abs(dx) > 4) panRef.current.moved = true;
            if (panRef.current.moved && wrapRef.current) wrapRef.current.scrollLeft = panRef.current.startScroll - dx;
        };
        const up = () => {
            didPan.current = !!(panRef.current && panRef.current.moved);
            panRef.current = null;
            if (wrapRef.current) wrapRef.current.classList.remove('panning');
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    }, [xToMs]);

    const handleSeekClick = useCallback((e) => {
        if (dragRef.current || !dataRef.current) return;
        if (didPan.current) { didPan.current = false; return; } // was a pan, not a click
        if (clickTimer.current) return; // second click of a double-click — let dblclick handle it
        const x = e.clientX;
        clickTimer.current = setTimeout(() => {
            clickTimer.current = null;
            const ms = xToMs(x);
            // Inside the active loop -> move the play cursor there but keep looping.
            if (activeLoopId != null && aStart != null && ms >= aStart && ms <= (aEnd || aStart)) {
                repositionLoop(ms);
                return;
            }
            if (refs.current.onSeek) refs.current.onSeek(ms);
        }, 220);
    }, [xToMs, activeLoopId, aStart, aEnd, repositionLoop]);

    const handleDblClick = useCallback((e) => {
        if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
        if (refs.current.onAddCue && dataRef.current) refs.current.onAddCue(xToMs(e.clientX));
    }, [xToMs]);

    // Right-click anywhere on the waveform → "Add cue / Add loop here".
    const handleContextMenu = useCallback((e) => {
        if (!dataRef.current) return;
        e.preventDefault(); e.stopPropagation();
        // Viewport coords + position:fixed so the waveform's overflow can't clip it.
        setCtxMenuPos(null);
        setCtxMenu({ x: e.clientX, y: e.clientY, ms: xToMs(e.clientX) });
    }, [xToMs]);
    // Flip/clamp the add-cue menu so it stays on-screen near an edge.
    useLayoutEffect(() => {
        if (!ctxMenu || !ctxMenuRef.current) { setCtxMenuPos(null); return; }
        setCtxMenuPos(positionMenuAtPoint(ctxMenuRef.current, ctxMenu.x, ctxMenu.y));
    }, [ctxMenu]);
    useEffect(() => {
        if (!ctxMenu) return undefined;
        const close = () => setCtxMenu(null);
        const onKey = (ev) => { if (ev.key === 'Escape') setCtxMenu(null); };
        window.addEventListener('click', close);
        window.addEventListener('keydown', onKey);
        return () => { window.removeEventListener('click', close); window.removeEventListener('keydown', onKey); };
    }, [ctxMenu]);

    useEffect(() => () => { if (clickTimer.current) clearTimeout(clickTimer.current); }, []);
    const startDrag = useCallback((e, cueId) => {
        e.stopPropagation(); e.preventDefault();
        dragRef.current = { id: cueId };
        const move = (ev) => { if (dragRef.current && refs.current.onMoveCue) refs.current.onMoveCue(dragRef.current.id, xToMs(ev.clientX)); };
        const up = () => { dragRef.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
        window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    }, [xToMs]);

    // Drag a loop edge ('start' | 'end').
    const startLoopEdgeDrag = useCallback((e, loopId, edge) => {
        e.stopPropagation(); e.preventDefault();
        dragRef.current = { id: loopId };
        const move = (ev) => { if (refs.current.onResizeLoop) refs.current.onResizeLoop(loopId, edge, xToMs(ev.clientX)); };
        const up = () => { dragRef.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
        window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    }, [xToMs]);

    // Drag a loop body to move it, preserving length. A plain click (no drag)
    // is NOT consumed here — it bubbles to the content's seek handler so clicking
    // inside a loop jumps to the clicked position (not the loop start).
    const startLoopBodyDrag = useCallback((e, loop) => {
        e.stopPropagation();
        dragRef.current = { id: loop.id };
        const grabOffset = xToMs(e.clientX) - loop.positionMs;
        const startX = e.clientX;
        let moved = false;
        const move = (ev) => {
            if (Math.abs(ev.clientX - startX) > 4) moved = true;
            if (moved && refs.current.onMoveLoop) refs.current.onMoveLoop(loop.id, xToMs(ev.clientX) - grabOffset);
        };
        const up = () => {
            dragRef.current = null;
            didPan.current = moved; // moved -> suppress the click-seek; click -> allow seek to position
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
    }, [xToMs]);

    // 10% steps (rounded to a clean grid), so clicks feel precise.
    const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + 0.1) * 10) / 10));
    const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - 0.1) * 10) / 10));

    return (
        <div className="colored-waveform">
            <div className="cw-canvas-wrap" ref={wrapRef}>
                <div
                    className="cw-content"
                    ref={contentRef}
                    style={{ width: `${zoom * 100}%` }}
                    onPointerDown={onContentPointerDown}
                    onClick={handleSeekClick}
                    onDoubleClick={handleDblClick}
                    onContextMenu={handleContextMenu}
                >
                    <canvas ref={canvasRef} className="cw-canvas" />

                    {/* loop regions (under the markers + playhead) */}
                    {status === 'ready' && duration > 0 && (loops || []).map((loop) => {
                        const leftPct = msToPct(loop.positionMs);
                        const rightPct = msToPct(loop.endPositionMs || loop.positionMs);
                        const color = loop.color || '#34C759';
                        return (
                            <div
                                key={loop.id}
                                className={`cw-loop ${loop.id === activeLoopId ? 'active' : ''} ${hoveredId === loop.id ? 'hover-sync' : ''}`}
                                style={{ left: `${leftPct}%`, width: `${Math.max(0, rightPct - leftPct)}%`, '--loop-color': color }}
                                onPointerDown={(e) => startLoopBodyDrag(e, loop)}
                                onDoubleClick={(e) => e.stopPropagation()}
                                onMouseEnter={() => onHoverItem && onHoverItem(loop.id)}
                                onMouseLeave={() => onHoverItem && onHoverItem(null)}
                                title={loopLabel(loop)}
                            >
                                <span className="cw-loop-handle start" onPointerDown={(e) => startLoopEdgeDrag(e, loop.id, 'start')} />
                                <span className="cw-loop-label">{loopLabel(loop)}</span>
                                <span className="cw-loop-handle end" onPointerDown={(e) => startLoopEdgeDrag(e, loop.id, 'end')} />
                            </div>
                        );
                    })}

                    {/* live shift-drag loop preview */}
                    {drawLoop && (() => {
                        const lo = Math.min(drawLoop.startMs, drawLoop.endMs);
                        const hi = Math.max(drawLoop.startMs, drawLoop.endMs);
                        const leftPct = msToPct(lo);
                        const rightPct = msToPct(hi);
                        return <div className="cw-loop cw-loop-drawing" style={{ left: `${leftPct}%`, width: `${Math.max(0, rightPct - leftPct)}%` }} />;
                    })()}

                    <div className="cw-playhead" ref={playheadRef} />
                    {status === 'ready' && duration > 0 && (cues || []).map((cue) => {
                        const isHot = cue.hotcueIndex != null && cue.hotcueIndex >= 0;
                        const frac = Math.min(1, Math.max(0, cue.positionMs / 1000 / duration));
                        const leftPct = (EDGE_FRAC + frac * (1 - 2 * EDGE_FRAC)) * 100;
                        const color = isHot ? (cue.color || '#00bcd4') : '#c8c8c8';
                        return (
                            <div
                                key={cue.id}
                                className={`cw-marker ${isHot ? 'hot' : 'memory'} ${hoveredId === cue.id ? 'hover-sync' : ''}`}
                                style={{ left: `${leftPct}%`, '--cue-color': color }}
                                onPointerDown={(e) => startDrag(e, cue.id)}
                                onClick={(e) => e.stopPropagation()}
                                onDoubleClick={(e) => e.stopPropagation()}
                                onMouseEnter={() => onHoverItem && onHoverItem(cue.id)}
                                onMouseLeave={() => onHoverItem && onHoverItem(null)}
                                title={`${isHot ? `Hot Cue ${cue.hotcueIndex + 1}` : 'Memory cue'}${cue.name ? ` — ${cue.name}` : ''}`}
                            >
                                <span className="cw-marker-flag">
                                    {isHot ? cue.hotcueIndex + 1 : 'M'}
                                    {/* Show the label on MEMORY cues only — that's the navigation
                                        roadmap where a name reads; hot pads stay clean (name is still
                                        in the tooltip above + exported to the cue comment). */}
                                    {!isHot && cue.name ? <span className="cw-marker-label">{cue.name}</span> : null}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {status === 'loading' && <div className="cw-overlay">Analyzing waveform…</div>}
                {status === 'error' && <div className="cw-overlay cw-error">Could not load audio</div>}

                {/* zoom controls (v2: portalled into the editor toolbar slot) */}
                {status === 'ready' && (() => {
                    const zoomUi = (
                        <div className="cw-zoom">
                            <button onClick={zoomOut} title="Zoom out" disabled={zoom <= MIN_ZOOM}>−</button>
                            <span className="cw-zoom-level">{Math.round(zoom * 100)}%</span>
                            <button onClick={zoomIn} title="Zoom in" disabled={zoom >= MAX_ZOOM}>+</button>
                        </div>
                    );
                    return zoomSlotEl ? ReactDOM.createPortal(zoomUi, zoomSlotEl) : zoomUi;
                })()}

                {/* right-click add menu */}
                {ctxMenu && (
                    <div
                        ref={ctxMenuRef}
                        className="cw-context-menu"
                        style={{
                            position: 'fixed',
                            left: `${ctxMenuPos ? ctxMenuPos.left : ctxMenu.x}px`,
                            top: `${ctxMenuPos ? ctxMenuPos.top : ctxMenu.y}px`,
                            visibility: ctxMenuPos ? 'visible' : 'hidden',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button onClick={() => { if (refs.current.onAddCue) refs.current.onAddCue(ctxMenu.ms, false); setCtxMenu(null); }}>
                            <span className="cw-ctx-cue-dot" />Add hot cue
                        </button>
                        <button onClick={() => { if (refs.current.onAddCue) refs.current.onAddCue(ctxMenu.ms, true); setCtxMenu(null); }}>
                            <span className="cw-ctx-cue-dot memory" />Add memory cue
                        </button>
                        {onAddLoop && (
                            <button onClick={() => { if (refs.current.onAddLoop) refs.current.onAddLoop(ctxMenu.ms); setCtxMenu(null); }}>
                                <LoopIcon className="cw-ctx-icon" />Add loop here
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* transport + cue pads strip */}
            <div className="cw-controls">
                <button className="cw-btn cw-play" onClick={handleTransport} title={(activeLoopId != null ? loopPlaying : isPlaying) ? 'Pause' : 'Play'}>
                    {(activeLoopId != null ? loopPlaying : isPlaying) ? <PauseIcon className="icon" /> : <PlayIcon className="icon" />}
                </button>
                <div className="cw-pads-slot">{children}</div>
                <span className="cw-time">{formatTime(curTime)} / {formatTime(duration)}</span>
            </div>
        </div>
    );
};

export default ColoredWaveform;
